import { useState, type FC, useEffect, useRef } from 'react';
import { useInjection } from 'inversify-react';
import * as Recharts from 'recharts';
import { observer } from 'mobx-react';
import styled from 'styled-components';

import { TYPES } from '@/engine/ioc';
import { useTheme } from '@mui/material/styles';
import ErrorIcon from '@mui/icons-material/Error';
import { CircularProgress } from '@mui/material';

import { type IPhasesCurveController, type PhaseData, CONTAINER_MARGIN, EXTENDED_CONTAINER_MARGIN } from './interfaces';
import { GraphKinds, PhaseTypes } from './interfaces';
import { CineloopControl } from './cineloopControl';
import { memoGetReferenceDotLabelPosition } from './utils/phaseCurveUtils.ts';

import { graphKindConfig, phaseTypeColorConfig } from './phaseCurve.config.ts';
import { getChartCurve } from '@/components/phasesCurve/components/chartCurve.tsx';
import { GraphButton } from '@/components/phasesCurve/components/graphButton.tsx';
import { GraphHeader } from '@/components/phasesCurve/components/graphHeader.tsx';
import type { CategoricalChartState } from 'recharts/types/chart/types';
import type { SelectChangeEvent } from '@mui/material';
import type { ViewsService } from '@/engine/app/Views.service.ts';

interface IProps {
  handleCurveIsRendered: (isRendered: boolean) => void;
}

export const PhasesCurve: FC<IProps> = observer(({ handleCurveIsRendered }) => {
  const theme = useTheme();

  const [isExpanded, setExpanded] = useState<boolean>(false);
  const [isShrinkGraphHidden, setIsShrinkGraphHidden] = useState(false);
  const [graphKind, setGraphKind] = useState<GraphKinds>(GraphKinds.RVVolume);
  const [isReferenceLinePicked, setIsReferenceLinePicked] = useState(false);
  const [grabbedReferenceLine, setGrabbedReferenceLine] = useState<PhaseTypes | undefined>(undefined);
  const [options, setOptions] = useState({ root: null, rootMargin: '0px', threshold: 0.1 });

  const viewsService: ViewsService = useInjection<ViewsService>(TYPES.ViewsService);
  const controller: IPhasesCurveController = useInjection<IPhasesCurveController>(TYPES.IPhasesCurveController);

  const [phasesData, endPhases] = controller.getData(graphKindConfig[graphKind]);

  const containerRef = useRef(null);
  const handleShrinkElVisibility = (entries: Array<IntersectionObserverEntry>): void => {
    const [entry] = entries;
    setIsShrinkGraphHidden(entry.isIntersecting);
    setExpanded(false);
  };

  useEffect(() => {
    handleCurveIsRendered(!(!phasesData.length || phasesData.length === 1 || !endPhases));
  }, [phasesData.length, endPhases, handleCurveIsRendered]);

  useEffect(() => {
    setOptions({ root: null, rootMargin: '0px', threshold: 0.1 });
  }, [viewsService.currentView]);

  useEffect(() => {
    const observer: IntersectionObserver = new IntersectionObserver(handleShrinkElVisibility, options);
    const current = containerRef.current;

    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions -- TODO: [chebotarev] fix eslint strict-boolean-expressions
    if (current) observer.observe(current);

    return (): void => {
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions -- TODO: [chebotarev] fix eslint strict-boolean-expressions
      if (current) observer.unobserve(current);
    };
  }, [containerRef, options]);

  if (!phasesData.length || phasesData.length === 1 || !endPhases) return null;

  const currentPhaseData: PhaseData | undefined = phasesData.find(
    (p: PhaseData): boolean => p.phase === controller.currentPhasePercentage
  );

  if (!currentPhaseData) {
    return <ErrorIcon />;
  }

  const _formatter = (value: string): [string, string] => {
    const val = Number(value);
    return ['', graphKindConfig[graphKind].unitsLabel + ': ' + val.toFixed() + graphKindConfig[graphKind].units];
  };

  const _labelFormatter = (value: string): string => {
    const val = Number(value);
    const phaseType = phasesData.find((p) => p.phase === val)?.type;
    const phaseTypeLabel = phaseType ? phaseTypeColorConfig[phaseType].label : '';
    return phaseTypeLabel + ' Phase: ' + val.toFixed() + '%';
  };

  const handleMouseDown = async (state: CategoricalChartState): Promise<void> => {
    if (state?.activeTooltipIndex !== undefined && !isReferenceLinePicked) {
      await controller.setPhase(state.activeTooltipIndex);
    }
    controller.initializeEndPositions();
  };

  const handleMouseUp = (): Promise<void> => {
    setGrabbedReferenceLine(undefined);
    controller.finalizeEndPositions();
    return Promise.resolve();
  };

  const handleMouseMove = (state: CategoricalChartState): Promise<void> => {
    if (
      !grabbedReferenceLine ||
      state?.activeTooltipIndex === undefined ||
      endPhases[grabbedReferenceLine].index === state.activeTooltipIndex
    ) {
      return Promise.resolve();
    }

    endPhases[grabbedReferenceLine].index = state.activeTooltipIndex;
    controller.setEndPositions({ ...endPhases });

    return Promise.resolve();
  };

  return (
    <>
      <div ref={containerRef}></div>
      <Container $isExpanded={isExpanded} data-testid="phaseCurve" aria-label="Phases-curve-chart">
        {isShrinkGraphHidden && (
          <>
            <GraphButton
              isExpanded={isExpanded}
              handleIsExpanded={(): void => {
                setExpanded(!isExpanded);
              }}
            />
            <GraphHeader
              isExpanded={isExpanded}
              graphKind={graphKind}
              handleKindChange={(event: SelectChangeEvent): void => {
                setGraphKind(event.target.value as GraphKinds);
              }}
            />
          </>
        )}

        <ChartContainer
          $isShrinkGraphHidden={isShrinkGraphHidden}
          className="chart-container"
          aria-label="chart-container"
        >
          <Recharts.ResponsiveContainer height={isExpanded ? EXTENDED_CONTAINER_MARGIN : CONTAINER_MARGIN} width="100%">
            <Recharts.LineChart
              style={{ cursor: grabbedReferenceLine ? 'col-resize' : 'default' }}
              data={phasesData}
              margin={{ top: 20, right: 30, left: isExpanded ? 5 : 30, bottom: 0 }}
              width={500}
              height={300}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
              onMouseDown={handleMouseDown}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
              onMouseMove={handleMouseMove}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
              onMouseUp={handleMouseUp}
            >
              {isExpanded && <Recharts.CartesianGrid strokeDasharray="3 3" />}
              {controller.isImagesLoadingCompleted && (
                <>
                  <Recharts.ReferenceLine
                    x={endPhases[PhaseTypes.Systole].phase}
                    stroke={phaseTypeColorConfig[PhaseTypes.Systole].color}
                    strokeWidth={1.5}
                    strokeDasharray="3 3"
                  />
                  <Recharts.ReferenceLine
                    style={{ cursor: 'col-resize' }}
                    x={endPhases[PhaseTypes.Systole].phase}
                    stroke="rgba(0,0,0,0)"
                    strokeWidth={5}
                    onMouseEnter={(): void => setIsReferenceLinePicked(true)}
                    onMouseLeave={(): void => setIsReferenceLinePicked(false)}
                    onMouseDown={(): void => {
                      setGrabbedReferenceLine(PhaseTypes.Systole);
                    }}
                    onMouseUp={(): void => {
                      setGrabbedReferenceLine(undefined);
                    }}
                  />
                  <Recharts.ReferenceLine
                    x={endPhases[PhaseTypes.Diastole].phase}
                    stroke={phaseTypeColorConfig[PhaseTypes.Diastole].color}
                    strokeWidth={1.5}
                    strokeDasharray="3 3"
                  />
                  <Recharts.ReferenceLine
                    style={{ cursor: 'col-resize' }}
                    x={endPhases[PhaseTypes.Diastole].phase}
                    stroke="rgba(0,0,0,0)"
                    strokeWidth={5}
                    onMouseEnter={(): void => setIsReferenceLinePicked(true)}
                    onMouseLeave={(): void => setIsReferenceLinePicked(false)}
                    onMouseDown={(): void => {
                      setGrabbedReferenceLine(PhaseTypes.Diastole);
                    }}
                    onMouseUp={(): void => {
                      setGrabbedReferenceLine(undefined);
                    }}
                  />
                </>
              )}
              <Recharts.XAxis
                hide={!isExpanded}
                axisLine={false}
                dataKey="phase"
                type="number"
                tickLine={false}
                domain={[0, 'dataMax']}
                unit="%"
                stroke={theme.palette.secondary.light}
                tickCount={phasesData.length}
                padding={{ left: 5, right: 5 }}
              />
              <Recharts.YAxis
                hide={!isExpanded}
                axisLine={false}
                tickLine={false}
                dataKey="weight"
                type="number"
                stroke={theme.palette.secondary.light}
                unit={graphKindConfig[graphKind].units}
              />
              {controller.isImagesLoadingCompleted ? (
                <Recharts.Tooltip
                  position={isExpanded ? { x: -1000, y: -1000 } : { x: 0, y: 60 }}
                  cursor={false}
                  separator=""
                  formatter={_formatter}
                  labelFormatter={_labelFormatter}
                  itemStyle={{ color: theme.palette.secondary.main }}
                  labelStyle={{ color: theme.palette.secondary.main }}
                  contentStyle={{ backgroundColor: 'inherit', border: '0', fontSize: '1.2em' }}
                />
              ) : null}

              {getChartCurve({
                phasesData,
                isReferenceLinePicked,
                isImagesLoadingCompleted: controller.isImagesLoadingCompleted,
                theme,
                grabbedReferenceLine
              })}

              <Recharts.ReferenceDot
                x={currentPhaseData.phase}
                y={currentPhaseData.weight}
                r={4}
                fill={
                  controller.isImagesLoadingCompleted
                    ? phaseTypeColorConfig[currentPhaseData.type].color
                    : theme.customPalette.objects.disabled
                }
                stroke="none"
                ifOverflow="extendDomain"
                label={{
                  value: isExpanded
                    ? currentPhaseData.weight.toFixed() + graphKindConfig[graphKind].units
                    : currentPhaseData.phase + '%',
                  position: memoGetReferenceDotLabelPosition({
                    phasesData,
                    phase: currentPhaseData.phase,
                    graphKind,
                    isExpanded
                  }),
                  fill: theme.palette.secondary.light
                }}
              />
            </Recharts.LineChart>
          </Recharts.ResponsiveContainer>
          {!controller.isImagesLoadingCompleted && <StyledProgress size={20} />}
        </ChartContainer>
      </Container>
      <CineloopControl
        isShrinkGraphHidden={isShrinkGraphHidden}
        isExpanded={isExpanded}
        disabled={!controller.isCineloopAvailable}
        isActive={controller.isCineloopActive}
      ></CineloopControl>
    </>
  );
});

const Container = styled.div<{ $isExpanded: boolean }>`
  background-color: ${({ theme }): string => theme.customPalette.panels.side.background};
  user-select: none;
  margin: 0 -0.5em 0;
  position: sticky;
  z-index: 4;
  top: -2px;
  min-height: 8em;
  @media (width < 100rem) {
    font-size: 0.85rem;
  }
`;

const ChartContainer = styled.div<{ $isShrinkGraphHidden: boolean }>`
  font-size: 0.7em;
  position: relative;
  margin-bottom: ${(props): string => (props.$isShrinkGraphHidden ? '2em' : '0')};
`;

const StyledProgress = styled(CircularProgress)`
  opacity: 0.5;
  position: absolute;
  inset: 0;
  margin: auto;
`;
